import React, { useEffect, useState, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { IonSpinner } from '@ionic/react';
import { exchangeCodeForTokens } from '../services/google';
import { PageShell } from '../components/ui/PageShell';

const GoogleAuthCallback: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double-execution in React Strict Mode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const handleCallback = async () => {
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const state = params.get('state'); // householdId
      const error = params.get('error');

      if (error) {
        setStatus('error');
        setErrorMessage(`Google authorization failed: ${error}`);
        return;
      }

      if (!code || !state) {
        setStatus('error');
        setErrorMessage('Missing authorization code or state parameter');
        return;
      }

      try {
        const tokens = await exchangeCodeForTokens(code, state);

        if (tokens) {
          setStatus('success');
          // Redirect back to settings after a short delay
          setTimeout(() => {
            history.replace('/settings');
          }, 1500);
        } else {
          setStatus('error');
          setErrorMessage('Failed to exchange authorization code for tokens');
        }
      } catch (err) {
        setStatus('error');
        setErrorMessage(err instanceof Error ? err.message : 'Unknown error occurred');
      }
    };

    handleCallback();
  }, [location.search, history]);

  return (
    <PageShell title="Google Authorization" headerType="page">
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '60vh',
        gap: 16,
        padding: 24,
        textAlign: 'center',
      }}>
        {status === 'loading' && (
          <>
            <IonSpinner name="crescent" style={{ width: 48, height: 48 }} />
            <h2 style={{ margin: 0, color: 'var(--ion-text-color)' }}>
              Connecting to Google...
            </h2>
            <p style={{ color: 'var(--ion-color-medium)', margin: 0 }}>
              Please wait while we complete the authorization.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'var(--ion-color-success)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 32,
            }}>
              ✓
            </div>
            <h2 style={{ margin: 0, color: 'var(--ion-text-color)' }}>
              Google Connected!
            </h2>
            <p style={{ color: 'var(--ion-color-medium)', margin: 0 }}>
              Redirecting you back to settings...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'var(--ion-color-danger)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 32,
              color: 'white',
            }}>
              ✕
            </div>
            <h2 style={{ margin: 0, color: 'var(--ion-text-color)' }}>
              Connection Failed
            </h2>
            <p style={{ color: 'var(--ion-color-danger)', margin: 0 }}>
              {errorMessage}
            </p>
            <button
              onClick={() => history.replace('/settings')}
              style={{
                marginTop: 16,
                padding: '12px 24px',
                borderRadius: 8,
                border: 'none',
                background: 'var(--ion-color-primary)',
                color: 'white',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Back to Settings
            </button>
          </>
        )}
      </div>
    </PageShell>
  );
};

export default GoogleAuthCallback;
